import { useState, useEffect, useMemo } from 'react';

const units = {
    days: 86400,
    hours: 3600,
    minutes: 60,
    seconds: 1,
};
export type UnitMap = typeof units;

export function calculateCountdown(endDate: string | null | undefined): UnitMap | null {
    if (!endDate) return null;
    let difference = Math.round((Date.parse(endDate) - Date.now()) / 1000);
    difference = Math.max(difference, 0);

    const output: UnitMap = {} as UnitMap;

    for (const [key, amount] of Object.entries(units)) {
        const fullUnits = Math.floor(difference / amount);

        output[key as keyof UnitMap] = fullUnits;

        difference -= fullUnits * amount;
    }

    return output;
}

export function countdownToText(unitsToShow: UnitMap): string {
    const suffixMap: Record<keyof UnitMap, string> = {
        days: 'd',
        hours: 'h',
        minutes: 'm',
        seconds: 's',
    };

    let output = '';

    let key: keyof UnitMap;
    for (key in unitsToShow) {
        if (unitsToShow.hasOwnProperty(key)) {
            const value = unitsToShow[key];
            if (value > 0 || output !== '') {
                const padding = value < 10;
                output += `${padding ? '0' : ''}${value}${suffixMap[key]} `;
            }
        }
    }

    return output ? output.trim() : '0s';
}

const getIsFinished = (countdown: Record<string, number> | null): boolean =>
    !!countdown && Object.values(countdown).every(value => value === 0);

export function useAuctionCountdown(
    endDate: string | null | undefined,
    loadOnFirstRender?: boolean
): { countdownUnits: UnitMap | null; countdownFinished: boolean | null } {
    const [unitMap, setUnitMap] = useState<UnitMap | null>(
        loadOnFirstRender ? calculateCountdown(endDate) : null
    );
    // memo is prob unnecessary but the interval timer is pretty short and this
    // hook may be called more than once in a tile, so let's reduce the amount
    // of recurring computations
    const countdownFinished = useMemo(() => getIsFinished(unitMap), [unitMap]);

    useEffect(() => {
        // 0 is safe for initial value bc "intervalID is a numeric, non-zero value"
        let id: number = 0;
        // do not start interval if countdown has finished
        if (endDate && !countdownFinished) {
            // if id === 0, execute leading setUnitMap
            if (id === 0) {
                setUnitMap(calculateCountdown(endDate));
            }
            id = window.setInterval(() => {
                const countdownUnits = calculateCountdown(endDate);
                setUnitMap(countdownUnits);
            }, 1000);
        }
        return () => clearInterval(id);
    }, [endDate, countdownFinished]);

    return {
        countdownUnits: unitMap,
        countdownFinished,
    };
}
